import { apiFetch, ApiClientError } from './client.js';
import { DEMO, demoUploadFiles } from '../demo/index.js';
import { archivosQuePasanElTope, mensajeArchivoDemasiadoGrande, type LimitesDeSubida } from '@supernova/shared';

export type ProjectIndexStatus =
  | 'idle'
  | 'chunking'
  | 'embedding'
  | 'ready'
  | 'failed'
  | 'partial';

export type ProjectSummaryStatus = 'idle' | 'analyzing' | 'ready' | 'failed';

export interface Project {
  id: string;
  tenantId: string;
  ownerUserId: string;
  name: string;
  description: string | null;
  status: 'active' | 'archived' | 'deleted';
  digestStatus: 'pending' | 'indexing' | 'ready' | 'failed';
  indexStatus: ProjectIndexStatus;
  chunksTotal: number;
  chunksEmbedded: number;
  indexedAt: string | null;
  obraAnalysis: Record<string, unknown> | null;
  summaryStatus: ProjectSummaryStatus;
  summaryAt: string | null;
  summaryError: string | null;
  totalFiles: number;
  totalSizeMb: number;
  createdAt: string;
  updatedAt: string;
  /** True si es el proyecto de ejemplo (demo): visible para todos y de solo lectura. */
  isExample?: boolean;
  /** Stats de la última cotización (presentes en el listado). */
  quoteConcepts?: number | null;
  quoteConceptsPriced?: number | null;
  quotedPct?: number | null;
  quotedAmount?: number | null;
  /** True si la última cotización ya fue importada a OPUS (m1). */
  exportedToOpus?: boolean;
  /** Disciplina de obra (contexto editado → fallback IA). */
  disciplina?: string | null;
  /** Id de la última cotización (para enlazar a Cotización/APUs). */
  latestQuoteId?: string | null;
  /** Estado de esa última cotización. Se usa para NO reescribir la selección de
   *  archivo fuente y catálogos de un presupuesto ya armado al entrar a verlo. */
  latestQuoteStatus?: string | null;
  /** Vista previa del plano principal (lista de OPUS ci). */
  portadaUrl?: string | null;
  /** Si las láminas de planos del proyecto se leen al generar precios con IA. */
  leerPlanos?: boolean;
  /** Láminas de planos detectadas en el análisis (0 = no hay). */
  planosLaminas?: number;
  /** Resumen legible de las láminas detectadas: "7 A3, 4 A2, 2 A1". */
  planosResumen?: string | null;
  /** Moneda de la obra (ISO 4217) o `null` si no se sabe. NUNCA un default:
   *  con `null` los importes se muestran sin símbolo. Ver `lib/money.ts`. */
  moneda?: string | null;
  /** 'contexto' = la declararon; 'catalogos' = la dicen los catálogos elegidos;
   *  'desconocida' = falta capturarla. */
  monedaOrigen?: 'contexto' | 'catalogos' | 'desconocida';
}

export interface ProjectFile {
  id: string;
  tenantId: string;
  projectId: string;
  originalName: string;
  storageKey: string;
  mimeType: string | null;
  sizeBytes: number | null;
  contentHash: string | null;
  classification: string | null;
  parseStatus: 'pending' | 'parsing' | 'ok' | 'failed' | 'skipped';
  parseError: string | null;
  parsedPages: number | null;
  /** Conceptos reales detectados (presente solo en el detalle del proyecto). */
  conceptsDetected?: number | null;
  ocrUsed: boolean;
  chunksCount: number;
  uploadedAt: string;
  parsedAt: string | null;
}

export async function listProjects(): Promise<Project[]> {
  const res = await apiFetch<{ projects: Project[] }>('/api/v1/projects');
  return res.projects;
}

export async function createProject(args: {
  name: string;
  description?: string;
}): Promise<Project> {
  const res = await apiFetch<{ project: Project }>('/api/v1/projects', {
    method: 'POST',
    body: args,
  });
  return res.project;
}

export async function getProject(
  id: string,
): Promise<{
  project: Project;
  files: ProjectFile[];
  /** Láminas que todavía no se leyeron: si es 0, generar no consume por planos. */
  planosPendientes?: number;
  planosCostoUsd?: number | null;
}> {
  return await apiFetch<{ project: Project; files: ProjectFile[] }>(`/api/v1/projects/${id}`);
}

export async function patchProject(
  id: string,
  patch: {
    name?: string;
    description?: string | null;
    status?: 'active' | 'archived';
    leerPlanos?: boolean;
  },
): Promise<Project> {
  const res = await apiFetch<{ project: Project }>(`/api/v1/projects/${id}`, {
    method: 'PATCH',
    body: patch,
  });
  return res.project;
}

export async function deleteProject(id: string): Promise<{ deletedFiles: number }> {
  return await apiFetch(`/api/v1/projects/${id}`, { method: 'DELETE' });
}

/**
 * Sube uno o varios archivos al proyecto.
 * Reporta progreso (0..1) si se pasa onProgress.
 */
/** Topes de subida de la API (se piden una vez por sesión de la pantalla). */
let limitesCache: Promise<LimitesDeSubida | null> | null = null;
export function getLimitesDeSubida(): Promise<LimitesDeSubida | null> {
  limitesCache ??= apiFetch<LimitesDeSubida>('/api/v1/uploads/limites').catch(() => {
    limitesCache = null; // sin topes (API vieja o sin red): la API igual revisa al recibir
    return null;
  });
  return limitesCache;
}

export async function uploadFiles(
  projectId: string,
  files: File[],
  onProgress?: (frac: number) => void,
): Promise<{
  files: ProjectFile[];
  deduplicated: Array<{ id: string; duplicateOf: string }>;
}> {
  // MODO DEMO: subida simulada — barra de progreso + lectura/clasificación/
  // indexado animados con los eventos reales del worker (ver demo/index.ts).
  if (DEMO) return demoUploadFiles(files, onProgress);

  // ANTES de mandar nada: un archivo que pasa su tope no se sube (la API lo rechazaría a media subida y
  // el navegador puede ver la conexión cortada en vez del mensaje). Los topes los da la API.
  const limites = await getLimitesDeSubida();
  const grande = limites ? archivosQuePasanElTope(limites, files)[0] : undefined;
  if (grande) {
    throw new ApiClientError(413, {
      code: 'FILE_TOO_LARGE',
      message: mensajeArchivoDemasiadoGrande(grande.nombre, grande.topeMb),
    });
  }

  return new Promise((resolve, reject) => {
    const fd = new FormData();
    for (const f of files) fd.append('files', f, f.name);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `/api/v1/projects/${projectId}/files`);
    xhr.withCredentials = true;
    xhr.responseType = 'text';

    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(e.loaded / e.total);
      };
    }

    xhr.onerror = () => reject(new ApiClientError(0, { code: 'NETWORK', message: 'Sin red' }));
    xhr.onabort = () => reject(new ApiClientError(0, { code: 'ABORTED', message: 'Cancelado' }));
    xhr.onload = () => {
      let body: unknown;
      try {
        body = xhr.responseText ? JSON.parse(xhr.responseText) : undefined;
      } catch {
        body = xhr.responseText;
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(
          body as {
            files: ProjectFile[];
            deduplicated: Array<{ id: string; duplicateOf: string }>;
          },
        );
      } else {
        reject(new ApiClientError(xhr.status, body));
      }
    };
    xhr.send(fd);
  });
}

export async function deleteFile(projectId: string, fileId: string): Promise<void> {
  await apiFetch(`/api/v1/projects/${projectId}/files/${fileId}`, { method: 'DELETE' });
}

/** Reintenta la lectura (parse) de un archivo que quedó en `failed`/`skipped`. */
/**
 * Detener la lectura de los documentos del proyecto. Además de frenar lo que corre, saca de
 * la cola lo que todavía no arrancó y LIMPIA lo parcial: un documento a medio indexar
 * parece listo y el sistema lo usaría para buscar. Vuelve a quedar pendiente para leerlo de
 * nuevo cuando se quiera.
 */
export async function stopIndexing(projectId: string): Promise<{ ok: boolean; jobsQuitados: number; archivos: number; trozos: number }> {
  return await apiFetch(`/api/v1/projects/${projectId}/indexing/stop`, { method: 'POST' });
}

export async function reparseFile(projectId: string, fileId: string): Promise<void> {
  await apiFetch(`/api/v1/projects/${projectId}/files/${fileId}/reparse`, { method: 'POST' });
}

/** Corrige la clasificación de un archivo (ej. marcar el catálogo de conceptos). */
export async function updateFileClassification(
  projectId: string,
  fileId: string,
  classification: string,
): Promise<{ ok: boolean; classification: string }> {
  return await apiFetch(`/api/v1/projects/${projectId}/files/${fileId}`, {
    method: 'PATCH',
    body: { classification },
  });
}

export function downloadUrl(projectId: string, fileId: string): string {
  return `/api/v1/projects/${projectId}/files/${fileId}/download`;
}
